function getUrlOrigin(url) {
  if (!url) return ''
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

// FE-3：endpoint 只留 path、去 query（new URL().pathname 天生不含 query/hash）、統一前導 /；
// 另去掉前兩段（版本號 + clientId，如 /v2/A1/self → /self），避免同一支 API 因 clientId 不同被拆成多個 issue。
// 已匯出共用：plugins/01.request.js 的 MalformedApiResponse 沿用同一套去敏／正規化規則。
export function getUrlEndpointPath(url) {
  if (!url) return ''
  try {
    const { pathname } = new URL(url)
    const parts = pathname.split('/').filter(Boolean)
    return '/' + parts.slice(2).join('/')
  } catch {
    return ''
  }
}

// FE-5：依正規化後的 path 標記業務類型，供看板篩選與 FE-2 分級 sample 使用
// 第一段以「(可選字首-)login」比對，涵蓋 login／guest-login／app-login 等變體，
// 避免 login 類型端點被漏判成 other 而被 FE-2 的 sample=0.4 隨機丟掉（違反「login 不能被抽掉」的目的）
function classifyErr998Endpoint(normalizedPath) {
  if (/^\/self(\/|$)/.test(normalizedPath)) return 'self'
  if (/^\/[a-z]*-?login(\/|$)/i.test(normalizedPath)) return 'login'
  return 'other'
}

export function getErr998SentryExtra({ endpoint, method, clientId, uidSite, clientIp } = {}) {
  const normalizedEndpoint = getUrlEndpointPath(endpoint)
  return {
    code: 'ERR_998',
    endpoint: normalizedEndpoint,
    method: method ?? '',
    site: clientId ?? '',
    ip: clientIp ?? '',
    apiHost: getUrlOrigin(endpoint),
    uidSite: uidSite ?? '',
    endpointClass: classifyErr998Endpoint(normalizedEndpoint),
    // 上報欄位：能走到 998 分支必然無 HTTP response（有 status 一律不進此分支），固定為 false
    hasResponse: false,
  }
}

// 測線可達性分類：ERR_998 只知道「某一條線這次沒回應」，無法分辨「該線劣化」與「裝置整體連不上網」。
// 兩者的後續處理完全不同（前者要查該線路 / 換線，後者屬使用者端網路狀況），但目前唯一的判斷依據
// hostTestResults 放在 extra，Sentry 不建索引、無法搜尋與篩選，只能逐筆事件展開人工看。
// 故把判斷結果收斂成低基數字串並以 tag 上報，讓看板能直接切分兩種情境。
const HOST_REACHABILITY = {
  UNKNOWN: 'unknown',
  ALL_DOWN: 'all_down',
  PARTIAL_DOWN: 'partial_down',
  ALL_UP: 'all_up',
}

// 同輪測線的容忍值：HostTester.ping() 的 testedAt 取自 ping 進入點，一輪內三條線幾乎同時發出，
// 落差在毫秒級；上一輪未結束的 ping 在新一輪 resetLastResults() 之後才寫回時，則會相差一個
// 測線週期（60 秒）。以此容忍值即可判斷 lastResults 是否為同一輪的完整結果。
const HOST_PROBE_SAME_ROUND_TOLERANCE_MS = 5000

// 對齊 host.client.js 的測線週期（跨檔約定：該處 timeout 若調整，此處需一併調整），
// 比照 suppressErr998Report.js 對齊同一週期的既有慣例。
const HOST_PROBE_INTERVAL_MS = 60 * 1000
// 新鮮度上限取兩個週期：一個週期是結果的自然老化（下一輪還沒開始），另一個是餘裕——
// 單輪 ping 若拖過 60 秒，host.client.js 的 isTestRunning 防重入會跳過下一次 tick，
// 使「當前這一輪」的 testedAt 合法地老到接近兩個週期。超過即代表 interval 根本沒在跑
// （分頁進背景 / 裝置休眠時瀏覽器會節流甚至凍結 Timer），結果不可信。
const HOST_PROBE_MAX_AGE_MS = 2 * HOST_PROBE_INTERVAL_MS

function classifyHostReachability(settledResults, hostCount) {
  if (settledResults.length !== hostCount) return HOST_REACHABILITY.UNKNOWN

  const latestTestedAt = Math.max(...settledResults.map((result) => result.testedAt))
  const isSameRound = settledResults.every((result) => latestTestedAt - result.testedAt <= HOST_PROBE_SAME_ROUND_TOLERANCE_MS)
  if (!isSameRound) return HOST_REACHABILITY.UNKNOWN

  const successCount = settledResults.filter((result) => result.status === 'success').length
  if (successCount === 0) return HOST_REACHABILITY.ALL_DOWN
  if (successCount < hostCount) return HOST_REACHABILITY.PARTIAL_DOWN
  return HOST_REACHABILITY.ALL_UP
}

/**
 * 摘要 HostTester 最近一次測線結果，供 ERR_998 上報標記可達性。
 * 結果殘缺（尚未測完、SSR 無測線器）、混到上一輪殘留、或已經過期時一律回 unknown，
 * 寧可不判斷也不給錯誤結論——這個 tag 會進 Sentry 索引，錯誤分類無法在查詢端補救。
 * @param {Array<{ index: number, status?: string, testedAt?: number }>} lastResults HostTester.lastResults
 * @param {number} [hostCount] 該站台設定的 API 線路數（HostTester.hosts.length）
 * @param {number} [now] 現在時間，供測試注入
 * @returns {{ reachability: string, probeAgeMs: number|null }} 可達性分類與測線資料的新鮮度
 */
export function summarizeHostProbe(lastResults, hostCount, now = Date.now()) {
  if (!Array.isArray(lastResults) || !hostCount) {
    return { reachability: HOST_REACHABILITY.UNKNOWN, probeAgeMs: null }
  }
  const settledResults = lastResults.filter((result) => result?.status && typeof result.testedAt === 'number')
  if (settledResults.length === 0) {
    return { reachability: HOST_REACHABILITY.UNKNOWN, probeAgeMs: null }
  }
  const probeAgeMs = now - Math.max(...settledResults.map((result) => result.testedAt))
  // 過期結果不得產生分類：例如裝置在最後一次 all_up 之後才斷網，若沿用舊資料，這筆 ERR_998
  // 會被標成 all_up 而污染索引；probeAgeMs 放在未索引的 extra，無法在查詢時把它濾掉。
  if (probeAgeMs > HOST_PROBE_MAX_AGE_MS) {
    return { reachability: HOST_REACHABILITY.UNKNOWN, probeAgeMs }
  }
  return { reachability: classifyHostReachability(settledResults, hostCount), probeAgeMs }
}

// FE-1：時間窗建議 5～15 分鐘，取中間值 10 分鐘。以模組層級 Map 實作，僅 client 端隨分頁存續，
// 比照 suppressAuthError.js 的既有慣例，SSR render 路徑略過，避免模組層級狀態隨 Node process 跨請求累積。
const SELF_DEDUPE_WINDOW_MS = 10 * 60 * 1000
const selfDedupeWindows = new Map()

// FE-1：/self 時間窗去重 —— 同一鑰匙（站台 + API 線 + self + 使用者）在時間窗內只允許送出 Sentry 一次；
// 之後的呼叫回傳 allow=false，並累計 suppressedCount，供有送出的那一筆記錄「被合併掉幾次」。
export function checkErr998SelfDedupe(key) {
  if (!import.meta.client) {
    return { allow: true, suppressedCount: 0 }
  }
  const now = Date.now()
  // 順手清掉過期 entry，避免長駐 server process / 分頁造成 Map 無限增長
  if (selfDedupeWindows.size > 500) {
    for (const [k, v] of selfDedupeWindows) {
      if (now - v.firstAt > SELF_DEDUPE_WINDOW_MS) selfDedupeWindows.delete(k)
    }
  }
  const existing = selfDedupeWindows.get(key)
  if (!existing || now - existing.firstAt > SELF_DEDUPE_WINDOW_MS) {
    // 開新窗口前，把上一個窗口累積的 suppressedCount 帶到這次要送出的報告上，
    // 否則故障期間被抑制的次數會在窗口交界處直接消失，看不出真實故障規模
    const carriedSuppressedCount = existing?.suppressedCount ?? 0
    selfDedupeWindows.set(key, { firstAt: now, suppressedCount: 0 })
    return { allow: true, suppressedCount: carriedSuppressedCount }
  }
  existing.suppressedCount += 1
  return { allow: false, suppressedCount: existing.suppressedCount }
}
